<template>
  <div>
    <v-menu offset-y class="mb-4">
      <template v-slot:activator="{ on }">
        <ButtonDefault :button-name="$t('shooting.buttons.download.download')" :loading="downloadLoading" v-on="on" />
      </template>
      <v-list>
        <!-- An unavailable entry is not given the Vuetify disabled prop: it would drop the entry from
             the menu keyboard navigation, and with it the only explanation of why it is unavailable.
             aria-disabled states it instead, and download() ignores the click. -->
        <v-list-item
          v-for="(item, index) in items"
          :id="`download-item-${index}`"
          :key="index"
          :class="{ 'download-item--unavailable': item.disabled }"
          :ripple="!item.disabled"
          :aria-disabled="item.disabled ? 'true' : undefined"
          :aria-describedby="item.info ? `download-info-${index}` : undefined"
          @click="download(item)"
        >
          <v-list-item-title :class="{ 'text--disabled': item.disabled }">{{ item.label }}</v-list-item-title>
          <v-tooltip v-if="item.info" bottom>
            <template v-slot:activator="{ on, attrs }">
              <!-- Stop the click here, so reading the explanation never starts a download. -->
              <span class="download-info-icon ml-2" v-bind="attrs" v-on="on" @click.stop>
                <v-icon small>mdi-information-outline</v-icon>
              </span>
            </template>
            <span>{{ item.info }}</span>
          </v-tooltip>
          <span v-if="item.info" :id="`download-info-${index}`" class="d-sr-only">{{ item.info }}</span>
        </v-list-item>
      </v-list>
    </v-menu>

    <PartialOptimizedDownloadModal
      :dialog="partialDialog"
      :optimized-count="optimizedCount"
      :total-count="optimizableCount"
      @close-dialog="closePartialDialog"
      @confirm-download="confirmPartialDownload"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import api from '@/script/api'
import { downloadLink, buildZipFileName } from '@/script/utils'
import { buildImagesZip, IMAGE_TYPES } from '@/script/zipImages'
import ButtonDefault from './common/atoms/ButtonDefault.vue'
import PartialOptimizedDownloadModal from './downloadButton/PartialOptimizedDownloadModal.vue'

const OLD_FLOW_TYPES = ['ORIGINAL_WITHOUT_PROCESSES', 'ORIGINAL_WITH_PROCESSES', 'STAMPED_WITH_PROCESSES']

const OLD_FLOW_LABEL_KEYS = {
  ORIGINAL_WITHOUT_PROCESSES: 'shooting.buttons.download.originalWithoutProcesses',
  ORIGINAL_WITH_PROCESSES: 'shooting.buttons.download.originalWithProcesses',
  STAMPED_WITH_PROCESSES: 'shooting.buttons.download.stampedWithProcesses'
}

const OPTIMIZED_STATES = Object.freeze({
  NONE: 'NONE',
  WAITING: 'WAITING',
  PARTIAL: 'PARTIAL',
  COMPLETE: 'COMPLETE'
})

export default {
  components: {
    ButtonDefault,
    PartialOptimizedDownloadModal
  },
  props: {
    shootingId: {
      type: String,
      default: ''
    },
    registration: {
      type: String,
      default: ''
    },
    date: {
      type: String,
      default: ''
    }
  },
  data: () => ({
    downloadLoading: false,
    partialDialog: false
  }),
  computed: {
    ...mapGetters(['originalImages', 'optimizedImages', 'processedImages']),
    // A shooting belongs to a flow, the corporation does not: a new flow corporation can still
    // own old flow shootings (see c26ece6). Only the images received tell the flow: optimized
    // images mean new flow, processed images mean old flow.
    isNewFlow() {
      return this.optimizedImages.length > 0
    },
    // Old flow with real processing (stamped, cropped, ...) downloads the legacy
    // server-side variants. A shooting with only originals has none of those, so
    // we must not offer downloads for processes that do not exist.
    hasProcessedImages() {
      return this.processedImages.length > 0
    },
    // Label photos (registration document, VIN plate) are dropped by every other processing
    // screen — see ReprocessingButton and Restamp. Counting a photo that never receives an
    // optimized version would hold the shooting at "incomplete" for ever and break AC4.
    optimizableImages() {
      return this.originalImages.filter((image) => !image.isLabelImage)
    },
    optimizableCount() {
      return this.optimizableImages.length
    },
    optimizedCount() {
      return this.optimizedImages.filter((image) => !image.isLabelImage).length
    },
    missingOptimizedCount() {
      // A reprocessing can leave more optimized photos than originals. Clamp, so the pop-up
      // never announces a negative amount.
      return Math.max(this.optimizableCount - this.optimizedCount, 0)
    },
    // The status gate for ACTO-168721. The pipeline handles one photo at a time and a photo can
    // stop reporting altogether, so remarketingShootingStatus is not yet a trustworthy
    // end-of-optimization signal. Until the Maestro consumer states its rules, treat
    // optimization as finished and release the subset. Read state.shootingStatus here to close
    // the gate, for example: return !['UPLOADING', 'AI_PROCESSING'].includes(this.shootingStatus)
    isOptimizationFinished() {
      return true
    },
    optimizedState() {
      if (this.optimizedCount === 0) return OPTIMIZED_STATES.NONE
      if (this.missingOptimizedCount === 0) return OPTIMIZED_STATES.COMPLETE
      return this.isOptimizationFinished ? OPTIMIZED_STATES.PARTIAL : OPTIMIZED_STATES.WAITING
    },
    optimizedItem() {
      const base = { type: IMAGE_TYPES.OPTIMIZED, label: this.$t('shooting.buttons.download.optimized') }

      switch (this.optimizedState) {
        // Nothing optimized: the download is not offered, and the icon states why.
        case OPTIMIZED_STATES.NONE:
          return { ...base, disabled: true, info: this.$t('shooting.buttons.download.noOptimizedYet') }

        // Kept for the day the status gate closes: the pipeline is still running.
        case OPTIMIZED_STATES.WAITING:
          return {
            ...base,
            disabled: true,
            info: this.$t('shooting.buttons.download.waitingForImages', { count: this.missingOptimizedCount })
          }

        // Some photos are missing: the download is allowed, after a confirmation.
        case OPTIMIZED_STATES.PARTIAL:
          return {
            ...base,
            disabled: false,
            requiresConfirmation: true,
            info: this.$t('shooting.buttons.download.partialDownloadText', {
              optimized: this.optimizedCount,
              total: this.optimizableCount
            })
          }

        // Every photo is optimized: download at once, with no warning.
        default:
          return { ...base, disabled: false }
      }
    },
    oldFlowItems() {
      return OLD_FLOW_TYPES.map((type) => ({ type, label: this.$t(OLD_FLOW_LABEL_KEYS[type]) }))
    },
    originalOnlyItems() {
      return [
        {
          type: 'ORIGINAL_WITHOUT_PROCESSES',
          label: this.$t('shooting.buttons.download.originalWithoutProcesses')
        }
      ]
    },
    newFlowItems() {
      return [
        { type: IMAGE_TYPES.ORIGINAL, label: this.$t('shooting.buttons.download.originalWithoutProcesses') },
        this.optimizedItem
      ]
    },
    items() {
      if (this.isNewFlow) return this.newFlowItems
      return this.hasProcessedImages ? this.oldFlowItems : this.originalOnlyItems
    }
  },
  methods: {
    async download(item) {
      if (item.disabled) return

      // State the amount before downloading an incomplete set. The download starts when the
      // user accepts the pop-up.
      if (item.requiresConfirmation) {
        this.partialDialog = true
        return
      }

      await this.runDownload(item.type)
    },
    closePartialDialog() {
      this.partialDialog = false
    },
    async confirmPartialDownload() {
      this.partialDialog = false
      await this.runDownload(IMAGE_TYPES.OPTIMIZED)
    },
    async runDownload(imageType) {
      this.downloadLoading = true

      try {
        const ok = this.isNewFlow ? await this.downloadFromClient(imageType) : await this.downloadFromServer(imageType)

        if (!ok) {
          this.showError()
          return
        }

        this.$mam.shootingsDownload(imageType)
      } catch (e) {
        this.showError()
      } finally {
        this.downloadLoading = false
      }
    },
    async downloadFromClient(imageType) {
      const images = imageType === IMAGE_TYPES.OPTIMIZED ? this.optimizedImages : this.originalImages

      if (!images.length) return false

      const blob = await buildImagesZip(images, this.registration)
      downloadLink(blob, buildZipFileName(this.date, this.registration))
      return true
    },
    async downloadFromServer(imageType) {
      const response = await api.downloadShooting(this.shootingId, this.registration, this.date, imageType)
      return Boolean(response)
    },
    showError() {
      this.$store.commit('SET_SNACKBAR', { snack: this.$t('api.commonError'), color: 'error' })
    }
  }
}
</script>

<style scoped>
/* Look unavailable without the Vuetify disabled prop: no pointer cursor, no hover overlay. */
.download-item--unavailable {
  cursor: default;
}

.download-item--unavailable::before {
  opacity: 0 !important;
}

.download-info-icon {
  cursor: help;
}
</style>
